import { useEffect, useState } from 'react';
import { Check, Sparkles } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

const PLANS = [
	{
		id: 'starter',
		title: 'Unify Starter',
		price: '£29',
		cadence: '/ month + VAT',
		blurb: 'For shops just getting going. Google, your Unify storefront, and Local Ads.',
		bullets: ['Google Shopping & Maps surfaces', 'Branded storefront', 'Local Ads with daily budget', 'Email support']
	},
	{
		id: 'pro',
		title: 'Unify Pro',
		price: '£79',
		cadence: '/ month + VAT',
		blurb: 'Everything in Starter, plus two paid channels included and the social allowance.',
		bullets: ['Everything in Starter', '2 paid channels included', 'Social channels allowance', 'Priority support'],
		recommended: true
	}
];

/**
 * Starter ↔ Pro plan picker.
 *
 * Two modes, driven by `status`:
 *   - `no_subscription` → "Subscribe to Unify" mode — picks a plan, confirms,
 *     and calls into NEA-5751's subscribe endpoint via the caller.
 *   - everything else   → "Change plan" mode — calls NEA-5591's change-plan
 *     endpoint via the caller. Pro → Starter is disabled in the UI because
 *     NEA-5591 currently rejects it (NEA-5771 follow-up).
 */
export function PlanChangeSheet({ open, onOpenChange, currentPlan, status, onConfirm, pending = false, returning = false }) {
	const isSubscribe = status === 'no_subscription' || currentPlan == null;
	const initial = isSubscribe ? 'pro' : currentPlan === 'starter' ? 'pro' : 'starter';
	const [selected, setSelected] = useState(initial);

	// The sheet's content stays mounted while `open` toggles, so `useState`'s
	// `initial` only sticks on the first render. Reset `selected` whenever the
	// sheet reopens (or the underlying plan changes) so a fresh open always
	// lands on the recommended swap target — not the prior selection.
	useEffect(() => {
		if (open) setSelected(initial);
	}, [open, initial]);

	const isDowngrade = !isSubscribe && currentPlan === 'pro' && selected === 'starter';
	const isSamePlan = !isSubscribe && selected === currentPlan;
	const ctaLabel = isSubscribe
		? `Subscribe to ${PLANS.find((p) => p.id === selected)?.title}`
		: isSamePlan
			? 'Already on this plan'
			: `Switch to ${PLANS.find((p) => p.id === selected)?.title}`;

	return (
		<Sheet open={open} onOpenChange={onOpenChange}>
			<SheetContent className="flex w-full flex-col gap-6 sm:max-w-lg">
				<SheetHeader>
					<SheetTitle>
						{isSubscribe ? (returning ? 'Restart your subscription' : 'Subscribe to Unify') : 'Change plan'}
					</SheetTitle>
					<SheetDescription>
						{isSubscribe
							? returning
								? 'Pick a plan to switch Unify back on for this location. Billing starts straight away — the free trial applies to new customers only.'
								: "Pick a plan to switch Unify on for this location. You'll get a 30-day trial before your first charge."
							: 'Switching takes effect on your next billing cycle — Stripe will prorate the difference automatically.'}
					</SheetDescription>
				</SheetHeader>

				<div className="space-y-3">
					{PLANS.map((plan) => {
						const isCurrent = !isSubscribe && plan.id === currentPlan;
						const isSelected = plan.id === selected;
						return (
							<button
								key={plan.id}
								type="button"
								onClick={() => setSelected(plan.id)}
								className={cn(
									'w-full rounded-2xl border bg-card p-5 text-left transition',
									isSelected
										? 'border-primary shadow-[0_0_0_2px_hsl(var(--primary)/0.2)]'
										: 'border-border hover:border-primary/50'
								)}
							>
								<div className="flex items-start justify-between gap-3">
									<div className="min-w-0">
										<div className="flex flex-wrap items-center gap-2">
											<div className="text-base font-bold text-foreground">{plan.title}</div>
											{plan.recommended ? (
												<Badge variant="info" className="gap-1">
													<Sparkles className="h-3 w-3" /> Recommended
												</Badge>
											) : null}
											{isCurrent ? <Badge variant="secondary">Current</Badge> : null}
										</div>
										<p className="mt-1.5 text-sm text-muted-foreground">{plan.blurb}</p>
									</div>
									<div className="text-right">
										<div className="text-xl font-extrabold tabular-nums text-foreground">{plan.price}</div>
										<div className="text-xs text-muted-foreground">{plan.cadence}</div>
									</div>
								</div>
								<ul className="mt-4 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
									{plan.bullets.map((b) => (
										<li key={b} className="flex items-start gap-2 text-foreground/80">
											<Check className="mt-0.5 h-4 w-4 text-success" />
											<span>{b}</span>
										</li>
									))}
								</ul>
							</button>
						);
					})}
				</div>

				{isDowngrade ? (
					<div className="rounded-xl border border-warning/40 bg-warning-soft p-3 text-xs text-warning-soft-foreground">
						Pro → Starter downgrades aren't self-serve yet — we need to walk through which paid channels move from{' '}
						<span className="font-semibold">included</span> to billable with you. Email{' '}
						<a className="font-semibold underline" href="mailto:support@unify.near.st">
							support@unify.near.st
						</a>{' '}
						and we'll sort it.
					</div>
				) : null}

				<SheetFooter>
					<Button variant="ghost" onClick={() => onOpenChange?.(false)} disabled={pending}>
						Cancel
					</Button>
					<Button
						variant={isSubscribe ? 'action' : 'default'}
						disabled={isSamePlan || isDowngrade || pending}
						onClick={() => onConfirm?.(selected)}
					>
						{pending ? 'Working…' : ctaLabel}
					</Button>
				</SheetFooter>
			</SheetContent>
		</Sheet>
	);
}
